import CompetitionTabHeader from "../../CompetitionTabHeader"
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import ActionLogs from "./ActionLogs"
import { capitalize } from "lodash"

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"

import { store as socketStore } from "components/Common/Socket/store"
import { entryUser } from "components/ReviewAudit/PublicRatingScope"
import { useToasts } from "react-toast-notifications"
import { connect } from "react-redux"

import { getPlayers as getPlayersAction } from "../../../../../../UserAccounts/PlayerAccounts/actions"
import { getEventDetails as getEventDetailsAction } from "../../../../../actions"
import { EVENT_DETAILS_READ_MODE_COMPETITION } from "../../../../../eventDetailsReadPolicy"
import { invalidateEventCache } from "../../../../../eventCachePolicy"
import EntryLists from "./EntryLists"
import EntryActionModal, { operationText } from "./EntryActionModal"
import EntryMoveAction from "./EntryMoveAction"
import useEntryOperation from "./useEntryOperation"
import AddEntryModal from "./AddEntryModal"
import Tenis10ChildEntryModal from "./Tenis10ChildEntryModal"
import Tenis10ChildWithdrawModal from "./Tenis10ChildWithdrawModal"
import { isLevel } from "./entryAdmissionText"
import WildCardControls from "./WildCardControls"
import useEntryAvailability from "./useEntryAvailability"
import EntryOperations from "./EntryOperations"
import AdmissionButton from "./AdmissionButton"
import { getLuckyDrawConfirmationCopy } from "./luckyDrawConfirmation"
import useDrawOperation from "../Draw/useDrawOperation"
import DrawOperationFeedback from "../Draw/DrawOperationFeedback"

export const Entries = ({
  competitionDetails,
  getPlayers,
  getEventDetails,
  players,
  playersLoading,
  playersError,
  userDetails,
  drawOperation,
  eventDetails = {},
  match,
}) => {
  const { id: eventId, competitionId } = match.params
  const { addToast } = useToasts()
  const refreshEventDetails = useCallback(
    (targetEventId = eventId) =>
      getEventDetails(
        targetEventId,
        EVENT_DETAILS_READ_MODE_COMPETITION,
        competitionId
      ),
    [competitionId, eventId, getEventDetails]
  )

  let filteredCompetition = competitionDetails || {}

  //add entry modal
  const [modal, setModal] = useState(false)
  const availability = useEntryAvailability({ eventId, competitionId, refreshKey: competitionDetails, isOpen: modal })
  const [loadingResults, setLoadingResults] = useState(false)
  const [results, setResults] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [searchError, setSearchError] = useState(false)
  const toggle = () => {
    setModal(!modal)
    setLoadingResults(false)
    setWildCard(false)
  }

  const globalState = useContext(socketStore)
  const {
    state: { socket: { socketClientId, socket } = {} } = {},
  } = globalState
  const localDrawOperation = useDrawOperation({ eventId, competitionId, userId: userDetails?.id || userDetails?._id,
    socket, socketClientId, refresh: refreshEventDetails, addToast, enabled: !drawOperation })
  const draw = drawOperation || localDrawOperation

  const { gameType } = filteredCompetition
  // The event's Singles/Doubles per player limit: null when the loaded event has none, undefined until the event is read.
  const event = filteredCompetition.eventDetails || eventDetails
  const entryLimit = event?._id ? event[gameType === "doubles" ? "doublesMaxNumber" : "singlesMaxNumber"] ?? null : undefined
  // P41 + P27: a Tenis10 competition takes children — the child entry of SportyaOS; a child its tenis10.ro family holds
  // (not claimed yet) is withdrawn through the same SportyaOS path.
  const isTenis10 = event?.programType === "tenis10"
  const [childEntry, setChildEntry] = useState(null)
  const [childWithdraw, setChildWithdraw] = useState(null)
  const openAdd = slotType => {
    if (isTenis10) return setChildEntry({ slotType })
    toggle()
    if (slotType === "WILD_CARD") setWildCard(true)
    return undefined
  }
  const familyHeldEntry = entryId => ["mainList", "waitingList", "preRegistrationList"]
    .flatMap(list => filteredCompetition[list] || []).find(entry => entry._id === entryId && entry.holder?.kind === "migrationFamily")
  const withdraw = entryId => { const held = familyHeldEntry(entryId); if (held) setChildWithdraw(held); else setEntryAction({ entryId, action: "withdraw" }) }
  // a claimed child's entry is withdrawn through the administrative operation, whose preview names only the parent who pays
  const listedEntry = entryId => ["mainList", "waitingList", "preRegistrationList"]
    .flatMap(list => filteredCompetition[list] || []).find(entry => entry._id === entryId)
  const notify = (notice, appearance = "success") => notice && addToast(notice, { appearance, autoDismiss: appearance === "success" })

  useEffect(() => {
    if (playersLoading) return
    setSearchError(Boolean(playersError) || players == null)
    if (!playersError && Array.isArray(players?.results)) {
      let playerList = []
      players.results
        .filter(player => player && player.status !== "deleted")
        .map(player =>
          playerList.push({
            value: `${player.firstName} ${player.lastName}`,
            label: setPlayerLabel(player),
            data: player,
          })
        )
      setResults(playerList)
    } else setResults([])
    setLoadingResults(false)
  }, [players, playersLoading, playersError]);

  const setPlayerLabel = (player) => {
    const label = [];
    label.push(`${player.firstName} ${player.lastName} (#${player?.userId})`);

    if (player.hasPenalty) {
      label.push('Player is currently suspended and cannot play. ');

      return label.join(' / ');
    }

    // add game level: the published search sends an unset game as null, the legacy one as {} or without the game
    const { singles, doubles } = player?.gameLevel || {};
    const levelOf = value => (isLevel(value) ? value : 'not set');
    if (isLevel(singles) || isLevel(doubles)) {
      label.push(`Singles: ${levelOf(singles)} / Doubles: ${levelOf(doubles)}`);
    } else {
      label.push('Level Not Set');
    }

    // add location
    if (player?.location?.name) {
      label.push(player?.location?.name);
    }

    // add membership
    label.push(`Plan: ${capitalize(player?.membership?.plan)}`);

    return label.join(' / ');
  }

  const handleSearch = useCallback((string) => {
    setSearchTerm(string)
    setSearchError(false)
    setResults([])
    setLoadingResults(true);
    getPlayers({
      sportType: eventDetails?.sportType,
      string,
      ignoreGameLevelSet: true,
    }, { page: 1, limit: 20 });
  }, [getPlayers, eventDetails?.sportType])


  //wild card
  const [wildCard, setWildCard] = useState(false)
  const [wildCardBusy, setWildCardBusy] = useState(false)

  const [entryAction, setEntryAction] = useState(null)
  const [moveState, setMoveState] = useState(null)
  // Closes the entry action and reads the lists again: after a confirmed change, or when a preview says the lists are out of
  // date (the entry, event or competition is gone).
  const reloadEntries = () => { invalidateEventCache({ eventId, competitionId }); setEntryAction(null); refreshEventDetails(eventId) }
  const operation = useEntryOperation({ eventId, competitionId, userId: userDetails?.id || userDetails?._id,
    onConfirmed: () => { setModal(false); reloadEntries() },
  })
  const submitEntry = ({ player, partner, quote, reason }) => !availability.reason(wildCard ? "addWildCard" : "addEntry") && operation.save({ action: "add", revision: quote.revision,
    payerUserId: player.data.userId, ...(gameType === "doubles" && { partnerUserId: partner.data.userId }), slotType: wildCard ? "WILD_CARD" : "STANDARD", reason })
  const recovery = operation.status === "unknown" && <div role="status">The final result has not been confirmed. {operation.message && <>{operation.message} </>}<Button color="link" onClick={operation.recover}>Recover operation</Button></div>
  const entryBusy = operation.pending || wildCardBusy || (entryAction?.action === "move" && (!moveState || moveState.checking))
  const failed = action => operation.status === "error" && operation.operation?.action === action && operation.operation?.entryId === entryAction?.entryId
  const moveOperationError = failed("move") ? operationText(operation) : null
  const withdrawError = failed("withdraw") ? { id: operation.operation.operationId, message: operationText(operation), refused: Boolean(operation.refused) } : null
  // Every failed add, as one object the Add modal scopes to the opening that sent it.
  const addError = operation.status === "error" && operation.operation?.action === "add"
    ? { id: operation.operation.operationId, code: operation.code, message: operation.message, refused: Boolean(operation.refused) } : null
  const submitMove = (input, preview) => operation.save(input)?.then(result => {
    if (result?.status === "saved" && preview.draw?.requiresRedraw) {
      addToast("Entry moved. Run Redraw before generating matches.", { appearance: "warning", autoDismiss: false })
    }
    return result
  })

  const Actions = ({ noMove, list, _id }) => {
    const moveReason = entryAction?.action === "move" && entryAction.entryId === _id ? moveState?.message || moveOperationError : undefined
    return <div className="entries-row-actions">
    <Button color="link" size="sm" disabled={entryBusy} onClick={() => withdraw(_id)}>Withdraw</Button>
    {!noMove && !familyHeldEntry(_id) && <span title={moveReason} tabIndex={moveReason ? 0 : undefined}>
      <Button color="link" size="sm" disabled={entryBusy || Boolean(moveReason)} aria-describedby={moveReason ? `entry-move-${_id}` : undefined}
        onClick={() => { setMoveState(null); setEntryAction({ entryId: _id, action: "move", destination: list }) }}>{list === "mainList" ? "Move to Main List" : "Move to Waiting List"}</Button>
    </span>}
  </div>
  }

  const [luckyDrawModal, setLuckyDrawModal] = useState(false)

  const toggleLuckyDrawModal = () => setLuckyDrawModal(!luckyDrawModal)
  // Events' draw places every Main List entry that is not withdrawn (getSortedPlayers) and refuses an empty list.
  // Until the competition read returns its Main List (the event shell carries only mainListCount), the button
  // stays disabled without claiming the list is empty.
  const mainListRead = Array.isArray(filteredCompetition.mainList)
  const mainListActive = mainListRead && filteredCompetition.mainList.some(entry => entry.status !== "WITHDRAWN")
  const luckyDrawReason = mainListRead && !mainListActive
    ? "Add at least one entry to the Main List before setting the Lucky Draw." : undefined
  const luckyDrawReasonId = `lucky-draw-reason-${competitionId}`

  // The competition card's "Operations" counter links to this section.
  const operationsShown = availability.operations !== undefined
  useEffect(() => {
    if (!operationsShown || window.location.hash !== "#operations") return
    const heading = document.getElementById("operations")
    heading?.scrollIntoView?.({ block: "start" })
    heading?.focus?.()
  }, [operationsShown])

  return (
    <>
      <CompetitionTabHeader title="Entries" />
      {operation.status && <div className={`alert alert-${operation.status === "error" ? "danger" : operation.status === "saved" ? "success" : "info"}`} role="status">
        <strong>{operation.status === "saved" ? "Entry updated" : operation.status === "error" || (operation.status === "unknown" && operation.message) ? operationText(operation) : "Waiting for the confirmed result…"}</strong>
        {operation.sporting && <div>Entry: {operation.sporting} · Payment: {operation.financial?.status || "pending"} · Notifications: {operation.notifications?.status || "unknown"}</div>}
        {operation.status === "unknown" && <Button color="link" onClick={operation.recover}>Check saved result</Button>}
      </div>}
      <EntryOperations eventId={eventId} competitionId={competitionId} operations={availability.operations}
        timezone={event?.timezone} onRetryLoad={availability.retry} onChanged={availability.refresh}
        onSettled={() => { invalidateEventCache({ eventId, competitionId }); refreshEventDetails(eventId) }} />
      <EntryLists competition={filteredCompetition} eventDetails={event} Actions={Actions}
        wildCardAction={position => <AdmissionButton id={`add-wc-${competitionId}-${position}`} color="link" className="p-0" aria-label={`Add wild card at position ${position}`}
          reason={entryBusy ? "Another entry operation is in progress." : availability.reason("addWildCard")} onClick={() => openAdd("WILD_CARD")}>Add wild card</AdmissionButton>}
        actionFeedback={entryAction?.action === "move" ? { entryId: entryAction.entryId, content: <EntryMoveAction key={entryAction.entryId} action={entryAction} eventId={eventId} competitionId={competitionId} pending={operation.pending}
          onStateChange={setMoveState} operationStatus={moveOperationError} onClose={() => setEntryAction(null)} onReload={reloadEntries} onConfirm={submitMove} /> } : null} toolbar={
        <div className="entries-toolbar-actions">
          <AdmissionButton id={`add-wc-${competitionId}`} color="secondary" reason={entryBusy ? "Another entry operation is in progress." : availability.reason("addWildCard")} onClick={() => openAdd("WILD_CARD")}>Add wild card</AdmissionButton>
          <AdmissionButton id={`add-entry-${competitionId}`} color="primary" reason={entryBusy ? "Another entry operation is in progress." : availability.reason("addEntry")} onClick={() => openAdd("STANDARD")}>Add entry</AdmissionButton>
          {availability.error && <div role="alert" className="text-danger">Could not check entry availability.
            <Button color="link" onClick={availability.retry}>Retry entry availability</Button>
          </div>}
          <WildCardControls key={competitionId} eventId={eventId} competitionId={competitionId} userId={userDetails?.id || userDetails?._id}
            refreshKey={competitionDetails} busy={entryBusy} onPending={setWildCardBusy}
            onConfirmed={() => { invalidateEventCache({ eventId, competitionId }); refreshEventDetails(eventId) }} />
        </div>
      } />
      <EntryActionModal action={entryAction?.action === "withdraw" ? entryAction : null} eventId={eventId} competitionId={competitionId} pending={operation.pending}
        withdrawError={withdrawError} recovery={recovery} onClose={() => setEntryAction(null)} onReload={reloadEntries} onConfirm={operation.save}
        child={entryAction?.action === "withdraw" && listedEntry(entryAction.entryId)?.profileId ? entryUser(listedEntry(entryAction.entryId)) : null} />
      <Tenis10ChildEntryModal isOpen={Boolean(childEntry)} slotType={childEntry?.slotType} eventId={eventId} competitionId={competitionId}
        disabledReason={childEntry ? availability.reason(childEntry.slotType === "WILD_CARD" ? "addWildCard" : "addEntry") : undefined}
        onClose={() => setChildEntry(null)} onEntered={(notice, appearance) => { setChildEntry(null); notify(notice, appearance); reloadEntries() }} />
      <Tenis10ChildWithdrawModal entry={childWithdraw} eventId={eventId} competitionId={competitionId}
        onClose={() => setChildWithdraw(null)} onWithdrawn={(notice, appearance) => { setChildWithdraw(null); notify(notice, appearance); reloadEntries() }} />
      <AddEntryModal
        competitionId={competitionId}
        eventId={eventId}
        gameType={gameType}
        level={filteredCompetition.level}
        restrictions={filteredCompetition.restrictions}
        entryLimit={entryLimit}
        slotType={wildCard ? "WILD_CARD" : "STANDARD"}
        pending={operation.pending}
        disabledReason={availability.reason(wildCard ? "addWildCard" : "addEntry")}
        onRetryAvailability={availability.error ? availability.retry : undefined}
        operationError={addError}
        recovery={recovery}
        isOpen={Boolean(modal)}
        loadingResults={loadingResults}
        searchError={searchError}
        onRetrySearch={searchTerm ? () => handleSearch(searchTerm) : undefined}
        onClose={toggle}
        onSearch={handleSearch}
        onSubmit={submitEntry}
        options={results}
      />
      <div className="d-flex flex-column">
        <div>
          <LuckyDrawModal
            operation={draw}
            open={luckyDrawModal}
            action={toggleLuckyDrawModal}
            hasExistingLuckyDraw={filteredCompetition?.luckyDraw?.length > 0}
          />

          <Button
            color="success"
            className="pl-1 pr-1 mb-4 mt-3"
            disabled={draw.locked || entryBusy || !mainListActive}
            aria-describedby={luckyDrawReason ? luckyDrawReasonId : undefined}
            onClick={toggleLuckyDrawModal}
          >
            Set lucky draw
          </Button>
          <DrawOperationFeedback operation={draw} competition={filteredCompetition}
            luckyDrawPath={`/events/${eventId}/competitions/${competitionId}/draw`} />
          {luckyDrawReason && <p id={luckyDrawReasonId} className="text-muted">{luckyDrawReason}</p>}
        </div>
        <ActionLogs
          competitionId={competitionId}
          filteredCompetition={filteredCompetition}
          ml={filteredCompetition.mainList}
          wl={filteredCompetition.waitingList}
          sl={filteredCompetition.preRegistrationList}
          timezone={event?.timezone}
        />
      </div>
    </>
  )
}

const LuckyDrawModal = ({
  open,
  action,
  operation,
  hasExistingLuckyDraw,
}) => {
  const confirmationCopy = getLuckyDrawConfirmationCopy(hasExistingLuckyDraw)

  return (
    <Modal isOpen={open} toggle={action}>
      <ModalHeader toggle={action}>{confirmationCopy.title}</ModalHeader>
      <ModalBody>{confirmationCopy.body}</ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          disabled={operation.locked}
          onClick={async () => {
            operation.run(hasExistingLuckyDraw ? "redraw" : "initial")
            action()
          }}
        >
          {confirmationCopy.confirmLabel}
        </Button>{" "}
        <Button color="secondary" onClick={action}>
          {confirmationCopy.cancelLabel}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const mapStateToProps = ({
  players: { players, searchLoading: playersLoading, searchError: playersError } = {},
  events: { events = [], eventDetails } = {},
  session: session = {},
}) => ({
  players,
  playersLoading,
  playersError,
  events,
  session,
})

export default connect(mapStateToProps, {
  getPlayers: getPlayersAction,
  getEventDetails: getEventDetailsAction,
})(Entries)
